"use client"

import { useState, useEffect } from "react"
import { Plus, Edit2, Trash2, Check, X, Map, Sparkles } from "lucide-react"
import { createState, getStates, updateState, deleteState as deleteStateApi, getCountries } from "../utils/api"

export default function State() {
  // States and countries data
  const [states, setStates] = useState([])
  const [countries, setCountries] = useState([])
  const [loadingStates, setLoadingStates] = useState(true)
  const [loadingCountries, setLoadingCountries] = useState(true)
  const [error, setError] = useState(null)
  // Form fields
  const [newState, setNewState] = useState("")
  const [selectedCountryId, setSelectedCountryId] = useState("")
  const [editingId, setEditingId] = useState(null)
  const [editingName, setEditingName] = useState("")
  // Track per-item actions
  const [savingId, setSavingId] = useState(null)
  const [deletingId, setDeletingId] = useState(null)

  // Fetch countries
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const res = await getCountries()
        const mapped = res.data.map((c) => ({ id: c._id || c.id, name: c.name }))
        setCountries(mapped)
      } catch (err) {
        setError(err.message)
      } finally {
        setLoadingCountries(false)
      }
    }
    fetchCountries()
  }, [])

  // Fetch states
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const res = await getStates()
        const mapped = res.data.map((s) => ({ id: s._id || s.id, name: s.name, countryId: s.country }))
        setStates(mapped)
      } catch (err) {
        setError(err.message)
      } finally {
        setLoadingStates(false)
      }
    }
    fetchStates()
  }, [])

  const addState = async () => {
    if (!newState.trim() || !selectedCountryId) return
    try {
      setSavingId("new") // Use a temporary ID for the new item being added
      // Payload uses 'country' field per backend validation
      const res = await createState({ name: newState.trim(), country: selectedCountryId })
      setStates((prev) => [...prev, { id: res.data._id, name: res.data.name, countryId: res.data.country }])
      setNewState("")
      setSelectedCountryId("")
    } catch (err) {
      setError(err.message)
    } finally {
      setSavingId(null)
    }
  }

  const deleteState = async (id) => {
    try {
      setDeletingId(id)
      await deleteStateApi(id)
      setStates((prev) => prev.filter((s) => s.id !== id))
    } catch (err) {
      setError(err.message)
    } finally {
      setDeletingId(null)
    }
  }

  const startEditing = (state) => {
    setEditingId(state.id)
    setEditingName(state.name)
  }

  const saveEdit = async () => {
    if (!editingName.trim()) return
    try {
      setSavingId(editingId)
      const res = await updateState(editingId, { name: editingName.trim() })
      setStates((prev) => prev.map((s) => (s.id === editingId ? { ...s, name: res.data.name } : s)))
      cancelEdit()
    } catch (err) {
      setError(err.message)
    } finally {
      setSavingId(null)
    }
  }

  const cancelEdit = () => {
    setEditingId(null)
    setEditingName("")
  }

  const handleKeyPress = (e, action) => e.key === "Enter" && action()

  if (loadingCountries || loadingStates) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-lg font-semibold text-gray-700">Loading data...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 py-8 px-4">
      <div className="w-full mx-auto space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="p-3 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl shadow-lg">
              <Map className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              State Management
            </h1>
          </div>
          <p className="text-gray-600 text-lg">Manage states and their associated countries</p>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-6 py-4 rounded-xl shadow-md flex items-center justify-between">
            <div className="flex items-center gap-2">
              <X className="w-5 h-5" />
              <strong className="font-bold">Error: </strong>
              <span>{error}</span>
            </div>
            <button onClick={() => setError(null)} className="text-sm font-medium underline hover:text-red-900">
              Dismiss
            </button>
          </div>
        )}

        {/* Add State Section */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-green-600 to-teal-600 p-6 text-white">
            <div className="flex items-center gap-2">
              <Plus className="h-6 w-6" />
              <h2 className="text-xl font-semibold">Add New State</h2>
            </div>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <label htmlFor="country-select" className="text-sm font-semibold text-gray-700">
                  Select Country
                </label>
                <select
                  id="country-select"
                  value={selectedCountryId}
                  onChange={(e) => setSelectedCountryId(e.target.value)}
                  className="w-full h-12 px-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg bg-white"
                  disabled={savingId === "new"}
                >
                  <option value="">-- Select Country --</option>
                  {countries.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2 md:col-span-2">
                <label htmlFor="state-name" className="text-sm font-semibold text-gray-700">
                  State Name
                </label>
                <input
                  id="state-name"
                  type="text"
                  placeholder="Enter state name"
                  value={newState}
                  onChange={(e) => setNewState(e.target.value)}
                  onKeyPress={(e) => handleKeyPress(e, addState)}
                  className="w-full h-12 px-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
                  disabled={savingId === "new"}
                />
              </div>
            </div>
            <button
              onClick={addState}
              disabled={!newState.trim() || !selectedCountryId || savingId === "new"}
              className="mt-6 w-full h-12 px-8 bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 text-white rounded-xl font-semibold disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition-all duration-200"
            >
              {savingId === "new" ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="w-5 h-5" />
                  Add State
                </>
              )}
            </button>
          </div>
        </div>

        {/* States List */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-orange-600 to-red-600 p-6 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="h-6 w-6" />
                <h2 className="text-xl font-semibold">States List</h2>
              </div>
              <div className="bg-white/20 px-4 py-2 rounded-full">
                <span className="font-semibold">
                  {states.length} {states.length === 1 ? "state" : "states"}
                </span>
              </div>
            </div>
          </div>

          <div className="p-6">
            {states.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-24 h-24 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Map className="w-12 h-12 text-orange-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No States Added Yet</h3>
                <p className="text-gray-600">Start by adding your first state above!</p>
              </div>
            ) : (
              <div className="space-y-4">
                {states.map((s, idx) => {
                  const country = countries.find((c) => c.id === s.countryId)
                  return (
                    <div
                      key={s.id}
                      className="group bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-xl p-4 transition-all duration-200 hover:shadow-md"
                    >
                      {editingId === s.id ? (
                        <div className="flex items-center gap-4">
                          <div className="flex items-center justify-center w-10 h-10 bg-orange-100 rounded-lg">
                            <span className="text-orange-600 font-bold text-sm">
                              {String(idx + 1).padStart(2, "0")}
                            </span>
                          </div>
                          <input
                            type="text"
                            value={editingName}
                            onChange={(e) => setEditingName(e.target.value)}
                            onKeyPress={(e) => handleKeyPress(e, saveEdit)}
                            autoFocus
                            className="flex-1 h-10 px-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            disabled={savingId === s.id}
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={saveEdit}
                              disabled={!editingName.trim() || savingId === s.id}
                              className="h-10 px-4 bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 text-white rounded-lg font-medium disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed flex items-center gap-2 shadow-md hover:shadow-lg transition-all duration-200"
                            >
                              {savingId === s.id ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                  Saving...
                                </>
                              ) : (
                                <>
                                  <Check className="w-4 h-4" />
                                  Save
                                </>
                              )}
                            </button>
                            <button
                              onClick={cancelEdit}
                              disabled={savingId === s.id}
                              className="h-10 px-4 bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white rounded-lg font-medium flex items-center gap-2 shadow-md hover:shadow-lg transition-all duration-200"
                            >
                              <X className="w-4 h-4" />
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="flex items-center justify-center w-10 h-10 bg-orange-100 rounded-lg">
                              <span className="text-orange-600 font-bold text-sm">
                                {String(idx + 1).padStart(2, "0")}
                              </span>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="w-6 h-6 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center">
                                <Map className="w-3 h-3 text-white" />
                              </div>
                              <div>
                                <span className="font-semibold text-gray-900 text-lg">{s.name}</span>
                                <span className="block text-sm text-gray-500">
                                  {country?.name || "Unknown Country"}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                            <button
                              onClick={() => startEditing(s)}
                              disabled={deletingId === s.id}
                              className="h-10 px-4 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white rounded-lg font-medium flex items-center gap-2 shadow-md hover:shadow-lg transition-all duration-200"
                            >
                              <Edit2 className="w-4 h-4" />
                              Edit
                            </button>
                            <button
                              onClick={() => deleteState(s.id)}
                              disabled={deletingId === s.id}
                              className="h-10 px-4 bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600 text-white rounded-lg font-medium flex items-center gap-2 shadow-md hover:shadow-lg transition-all duration-200"
                            >
                              {deletingId === s.id ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                  Deleting...
                                </>
                              ) : (
                                <>
                                  <Trash2 className="w-4 h-4" />
                                  Delete
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
